import type { IAppDetailsAppData } from '../common-types';
import { styled } from '@mui/material';
import { ButtonsList } from '../AppDetails';

const Container = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(3)} 0`,
}));

type ButtonsStripProps = {
  app: IAppDetailsAppData;
  basePath: string;
  asPath: string;
  origin: string;
  eventHandlers: {
    handleRecommendButtonClick: () => void;
    handleDownloadButtonClick: () => void;
    handleGetLinkButtonClick: () => void;
    handleFavouriteButtonClick: {
      handleAddFavouriteClick: () => void;
      handleRemoveFavouriteClick: () => void;
    };
  };
  isAppFavourited: boolean;
  isFavouritesLoading: boolean;
  translatedText: {
    removeFavouriteButton: {
      label: string;
      title: string;
    };
    addFavouriteButton: {
      label: string;
      title: string;
    };

    recommendButton: {
      label: string;
      title: string;
    };
    downloadButton: {
      label: string;
      title: string;
    };
    getLinkButton: {
      label: string;
      title: string;
    };
  };
};

/**
 * ButtonsStrip from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param basePath is required
 * @param asPath is required
 * @param origin is required, example "https://example.com"
 * @param translatedText is required, this is the text that as already been translated by the consumer of this component.
 */

export const ButtonsStrip = ({
  eventHandlers,
  isAppFavourited,
  isFavouritesLoading,
  basePath,
  asPath,
  origin,
  translatedText,
  ...props
}: ButtonsStripProps) => {
  const {
    handleRecommendButtonClick,
    handleDownloadButtonClick,
    handleFavouriteButtonClick,
    handleGetLinkButtonClick,
  } = eventHandlers ?? {};

  const {
    removeFavouriteButton,
    addFavouriteButton,
    recommendButton,
    downloadButton,
    getLinkButton,
  } = translatedText ?? {};

  const favouriteButtonProps = handleFavouriteButtonClick
    ? {
        isFavourited: isAppFavourited,
        isLoading: isFavouritesLoading,
        removeFavouriteButtonLabel: removeFavouriteButton?.label ?? '',
        handleRemoveFavouriteClick:
          handleFavouriteButtonClick.handleRemoveFavouriteClick,
        addFavouriteButtonLabel: addFavouriteButton?.label ?? '',
        handleAddFavouriteClick:
          handleFavouriteButtonClick.handleAddFavouriteClick,
      }
    : undefined;

  const recommendButtonProps = handleRecommendButtonClick
    ? {
        onClick: handleRecommendButtonClick,
        label: recommendButton?.label,
        title: recommendButton?.title,
      }
    : undefined;

  const downloadButtonProps = handleDownloadButtonClick
    ? {
        onClick: handleDownloadButtonClick,
        label: downloadButton?.label,
        title: downloadButton?.title,
      }
    : undefined;

  const getLinkButtonProps = handleGetLinkButtonClick
    ? {
        onClick: handleGetLinkButtonClick,
        label: getLinkButton?.label,
        title: getLinkButton?.title,
        link: origin ? origin + basePath + asPath : '',
      }
    : undefined;

  return (
    <Container {...props}>
      <ButtonsList
        favouriteButton={favouriteButtonProps}
        recommendButton={recommendButtonProps}
        downloadButton={downloadButtonProps}
        getLinkButton={getLinkButtonProps}
      />
    </Container>
  );
};
